/**
 * @namespace Browser 
 */
import { DeviceTypeEnum, HeaderDeviceTypeEnum, MobilePlatform } from '../../constants/DeviceTypeEnum'

export const Browser = {

    /** 
     * @typedef WindowDimensions
     * @property {Number} heigth Is user loged in or not
     * @property {Number} width Token
     */

    /** Get Browser window dimensions
     * @returns {WindowDimensions} Height and width browser window
     */
    getWindowDimensions() {
        const w = window,
            d = document,
            e = d.documentElement,
            g = d.getElementsByTagName('body')[0],
            x = w.innerWidth || e.clientWidth || g.clientWidth,
            y = w.innerHeight || e.clientHeight || g.clientHeight;
        return { heigth: y, width: x };
    },

    /** Check if client use tablet size browser
     * @returns {bool} True if browser width is less than 1024px
     */
    isTablet() {
        return Browser.getWindowDimensions().width < 1024;
    },
    /** Check if client is PWApp application
     * @returns {bool} True if the client is PWApp
     */
    isPWApp() {
        const PWAPP_IDENTIFICATOR = "MobApp";
        const userAgentString = Browser.getUserAgentString();
        return !!userAgentString?.includes(PWAPP_IDENTIFICATOR);
    },
    /** Check if client is Mobile Native Application
     * @returns {bool} True if the client is Mobile Native Application
     */
    isMobNativeApp() {
        const NATIVE_APP_IDENTIFICATOR = "MobNative";
        const userAgentString = Browser.getUserAgentString();
        return !!userAgentString?.includes(NATIVE_APP_IDENTIFICATOR);
    },
    isMobileApp() {
        return Browser.isPWApp() || Browser.isMobNativeApp();
    },
    /** Check if client is iOS web view
     * @returns {bool} True if the client is iOS web view
     */
    isIosWebView() {
        const isIos = Browser.getMobilePlatform() === MobilePlatform.IOSWeb;
        const isStandalone = window.navigator.standalone;

        const isSafari = /Safari/.test(navigator.userAgent) && !/CriOS/.test(navigator.userAgent);
        // Common browser features missing in web views 
        const lacksFullscreenAPI = !('requestFullscreen' in document.documentElement);

        //On iOS, in-app browsers and WebViews frequently exclude "Safari" from the user agent, even though they still contain "iPhone", "iPad", or "iPod".
        return isIos && (!isSafari || isStandalone || lacksFullscreenAPI);
    },
    /** Detects from user agent string is it crawler bot or not
     * @link https://udger.com/resources/ua-list/crawlers
     * @param {string } userAgent User uganet string
     * @returns {bool} True if user agent string belongs to crawler bot
     */
    isCrawelerBot(userAgent) {
        const botPattern = `/bot|googlebot|yandex|bing|yahoo|teoma|slurp|duckduckbot|baidu|crawler|spider|robot|crawling|mail.ru|validator|ia_archiver|yeti/`;
        let regexp = new RegExp(botPattern, 'i');
        return regexp.test(userAgent);
    },

    /** Check if client use landscape size browser
     * @returns {bool} True if browser width is more than height
     */
    isLandscape() {
        var object = window.screen.orientation || window.screen.msOrientation || window.screen.mozOrientation || null;
        if (object) {
            if (object.type.indexOf('landscape') !== -1) { return true; }
            if (object.type.indexOf('portrait') !== -1) { return false; }
        }
        if ('orientation' in window) {
            var value = window.orientation;
            if (value === 0 || value === 180) {
                return false;
            } else if (value === 90 || value === 270) {
                return true;
            }
        }
        // fallback to comparing width to height
        return window.innerWidth > window.innerHeight;
    },

    /** Detects user agent string
     * @returns {string} Returns user ugent string
     */
    getUserAgentString() {
        return navigator.userAgent || navigator.vendor || window.opera;
    },
    /** Detects domain of the site without subdomains
      * @returns {string} Returns user current domain
      */
    getDomain(subDomainIncluded = true, excludeWWW = true) {
        let currentDomain = ""
        const { host } = window.location;

        if (subDomainIncluded) {
            currentDomain = host;
        } else {

            let separate = host.split(".");

            if (separate.length > 2) {
                separate = separate.slice(separate.length - 2, separate.length);
                currentDomain = separate.join('.');
            }
        }

        currentDomain = currentDomain.toLowerCase();

        if (excludeWWW && currentDomain.startsWith('www.')) {
            currentDomain = currentDomain.substring(4)
        }

        return currentDomain;
    },
    /** Returns absolute url of web page
     * @returns {string} Returns absolute url of web page
     */
    getAbsoluteUrl() {
        return window?.location?.href;
    },
    /** Detects url query string
     * @returns {string} Returns url query string
     */
    getUrlQueryString() {
        return window?.location?.search;
    },
    generateQueryStringFromObject(params) {
        return Object.keys(params)
            .map(key => {
                const value = params[key];
                // Encode both key and value to ensure special characters are handled properly
                return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
            })
            .join('&');
    },
    /** Get object from query string
     * @returns {object} Returns url query string
     */
    getObjectFromQueryString(qs) {
        const queryString = qs || Browser.getUrlQueryString();
        const params = new URLSearchParams(queryString);
        const obj = {};

        for (let [key, value] of params) {
            // Check if the key already exists in the object
            if (obj.hasOwnProperty(key)) {
                // If it's already an array, push the new value
                if (Array.isArray(obj[key])) {
                    obj[key].push(value);
                } else {
                    // If it's not an array, convert it to an array with the existing value and the new value
                    obj[key] = [obj[key], value];
                }
            } else {
                // If the key doesn't exist, simply add it to the object
                obj[key] = value;
            }
        }
        return obj;
    },
    /** Detects origin domain of the site with protocol
     * @returns {string} Returns browser origin domain  with protocol
     */
    getOriginDomain() {
        return window.location.origin;
    },
    /**
     * Get main domain from the provided url.
     * This function returns main domain without sub domains fromurl
     * @param {string } url Url 
     * @returns {string} Domain
     */
    getMainDomain(url) {
        if (url) {
            //find & remove protocol (http, ftp, etc.) and get hostname
            if (url.indexOf("//") > -1) {
                url = url.split('/')[2];
            }
            else {
                url = url.split('/')[0];
            }

            const index = url.indexOf('/');
            if (index > 0) {
                url = url.substring(0, index);
            }

            let splitedHost = url.split(".");
            if (splitedHost.length > 2) {
                splitedHost = splitedHost.slice(splitedHost.length - 2, splitedHost.length);
                return splitedHost.join('.');
            }
        }

        return url;
    },
    /**
     * Returns current url protcol
     * @returns {string} Url protocol
     */
    getProtocol() {
        return window.location.protocol;
    },
    /**
     * Add param to provided URL
     * If URL is not specified current window URL will be returned by added param 
     * @param {string } name Param name
     * @param {string} value Param value 
     * @returns {string} New URL with added param
     */
    addParamToUrl(name, value, url = window.location.href) {
        if (url && url.startsWith('/')) {
            url = window.location.origin + url;
        }
        var u = new URL(url);
        u.searchParams.append(name, value);
        return u.href;
    },
    /**
     * Check is provided URL absloute url or not.
     * This function returns true if provided URL is absolute URL
     * @param {string } url URL 
     * @returns {Boolean}
     */
    isAbsoluteURL(url) {
        var r = new RegExp('^(?:[a-z]+:)?//', 'i');
        return r.test(url);
    },
    /**
     * Check is provided URL absloute url or not.
     * This function returns true if provided URL is absolute URL
     * @param {string } url URL 
     * @returns {string } Abslute URL 
     */
    convertToAbsoluteURL(url) {
        if (Browser.isAbsoluteURL(url)) {
            return url;
        }
        return `//${url}`;
    },
    /**
     * Determine the mobile operating system.
     * This function returns one of 'iOS', 'Android', 'Windows Phone', or 'unknown'.
     *
     * @returns {String}
     */
    getDeviceTypeByUserAgent(userAgent) {
        // var userAgent = navigator.userAgent || navigator.vendor || window.opera;
        //Windows Phone must come first because its UA also contains "Android"
        if (/windows phone/i.test(userAgent)) {
            return "Windows Phone";
        }
        if (/android/i.test(userAgent)) {
            return DeviceTypeEnum.MobileAndroid;
        }
        // iOS detection from: http://stackoverflow.com/a/9039885/177710
        if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
            return DeviceTypeEnum.MobileIOS;
        }
    },
    /**
    * Determines the mobile operating system and whether it is a native app or web-based.
    * This function returns one of the following based on the user agent:
    * - 'AndroidApp'
    * - 'AndroidWeb'
    * - 'IOSApp'
    * - 'IOSWeb'
    * - 'null' (if no match is found)
    *
    *  @returns {number|null} The platform type as defined in the `MobilePlatform` enum, or `null` if no match is found.
    */
    getMobilePlatform() {
        // Retrieve the user agent string
        const userAgent = Browser.getUserAgentString();
        // Check if the platform is a mobile native app
        const isNativeApp = Browser.isMobNativeApp(userAgent);

        // Detect Android platform
        if (/android/i.test(userAgent)) {
            return isNativeApp ? MobilePlatform.AndroidApp : MobilePlatform.AndroidWeb;
        }

        // Detect iOS platform (iPhone, iPad, iPod)
        if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
            return isNativeApp ? MobilePlatform.IOSApp : MobilePlatform.IOSWeb;
        }

        // If no platform detected, return platformType (null or unknown)
        return null;
    },

    getDeviceType() {
        return Browser.isTablet() ? DeviceTypeEnum.Mobile : DeviceTypeEnum.Desktop;
    },

    getHeaderDeviceType() {
        const deviceType = Browser.isMobileDevice() ? HeaderDeviceTypeEnum.Mobile : (Browser.isTablet() ? HeaderDeviceTypeEnum.Mobile : HeaderDeviceTypeEnum.Desktop);
        return deviceType;
    },

    isMobileDevice() {
        let check = false;
        (function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true; })(navigator.userAgent || navigator.vendor || window.opera);
        return check;
    },
    /**
     * Get url param value by name.
     * @param {string } paramName User uganet string
     * @returns {string} value of parameter
     */
    getURLParamValue(paramName, caseInsensative = true) {
        var urlString = window.location.href;
        var url = new URL(urlString);
        let urlParams;

        if (caseInsensative) {
            const lowCaseParams = new URLSearchParams()
            for (const [name, value] of url.searchParams) {
                lowCaseParams.append(name.toLowerCase(), value);
            }
            urlParams = lowCaseParams;
            paramName = paramName?.toLowerCase()
        } else {
            urlParams = url.searchParams;
        }

        var value = urlParams.get(paramName);

        return value;
    },

    isDomainWhiteListed(whiteListedDomains, domain) {
        if (Array.isArray(whiteListedDomains) && whiteListedDomains.includes(domain)) {
            return true;
        }
        return false;
    }
}
